import qs from 'qs';

export type QueryObj = Record<string, string | string[] | number | number[] | boolean | undefined>;

function strToUrl(str: string) {
	return new URL(str.startsWith('http://') ? str : `http://localhost/${str}`.replaceAll(/(\/\/+)/g, '/'));
}

function isActive(search: string, paths: string[]): boolean {
	const currentUrl = strToUrl(search);

	return paths.some((it) => {
		const path = strToUrl(it);
		const pathStatusIsSet = () => Boolean(path.searchParams.get('status'));
		const statusesAreNotEqual = () => path.searchParams.get('status') !== currentUrl.searchParams.get('status');
		const valuesAreMatching = () =>
			Array.from(currentUrl.searchParams.entries()).every(
				([key, value]) => !path.searchParams.has(key) || value === path.searchParams.get(key),
			);

		if (!currentUrl.pathname.startsWith(path.pathname)) return false;

		if (pathStatusIsSet() && statusesAreNotEqual()) return false;

		const currentUrlQueryLength = Array.from(currentUrl.searchParams.keys()).length;
		const pathUrlQueryLength = Array.from(path.searchParams.keys()).length;

		if (!currentUrlQueryLength || !pathUrlQueryLength) return true;

		return currentUrlQueryLength >= pathUrlQueryLength && valuesAreMatching();
	});
}

const buildUrl = (path: string, params: object): string =>
	Object.entries(params).reduce((acc, [key, value = '']) => acc.replace(`[${key}]`, value.toString()), path);

function stringifyQuery(query: QueryObj): string {
	const result = new URLSearchParams();

	Object.entries(query).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			if (Array.isArray(value)) {
				value.length && result.append(`${key}[]`, value.join(','));
			} else {
				result.append(key, value.toString());
			}
		}
	});

	return result
		.toString()
		.replace(/%5B%5D/g, '[]')
		.replace(/%2C/g, ',');
}

function stringifyQueryString(input: Record<string, any>) {
	return qs.stringify(input, {
		arrayFormat: 'brackets',
		encode: false,
		skipNulls: true,
	});
}

function stringifyQueryForAPI(query: QueryObj): string {
	const result = new URLSearchParams();

	Object.entries(query).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			if (Array.isArray(value)) {
				value.length && value.forEach((item) => result.append(`${key}[]`, item.toString()));
			} else {
				result.append(key, value.toString());
			}
		}
	});

	return result
		.toString()
		.replace(/%5B%5D/g, '[]')
		.replace(/%2C/g, ',');
}

function parseQuery<T extends QueryObj>(string: string) {
	const search = string.includes('?') ? string.split('?')[1] : string;
	const urlSearchParams = new URLSearchParams(search);
	const entries = urlSearchParams.entries() as IterableIterator<[string, string | string[]]>;

	const query = Array.from(entries).reduce((res, [key, value]) => {
		if (key.slice(-2) === '[]') {
			key = key.slice(0, -2);
			value = (value as string).split(',');
		}
		return {...res, [key]: value};
	}, {});

	return query as T;
}

type UrlSearchParamsInput = Record<string, string | number | boolean | null | undefined>;

function utmUrlSearchParams(href: string, params: UrlSearchParamsInput): string {
	try {
		const hadRootOnly = /^https?:\/\/[^/?#]+\/?(?:[?#].*)?$/.test(href);
		const parsed = new URL(href);
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				parsed.searchParams.set(key, String(value));
			}
		});
		const str = parsed.toString();
		if (hadRootOnly && parsed.pathname === '/') {
			return str.replace(/^(https?:\/\/[^/?#]+)\/(?=[?#]|$)/, '$1');
		}
		return str;
	} catch {
		return href;
	}
}

export const url = {
	stringifyQuery,
	stringifyQueryForAPI,
	stringifyQueryString,
	parseQuery,
	buildUrl,
	isActive,
	utmUrlSearchParams,
};
